import { spreadOnlyWinsWhen, ecnWinsWhen, verdictQuickRef } from '../data';

const strengthStyles: Record<string, { bg: string; text: string }> = {
  Strong: { bg: 'bg-accent-50/80', text: 'text-accent-700' },
  Neutral: { bg: 'bg-secondary-50/80', text: 'text-secondary-700' },
  Mild: { bg: 'bg-primary-50/80', text: 'text-primary-700' },
};

export default function WhenEachWinsSection() {
  return (
    <section className="relative w-full bg-background-50 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">
        {/* Header */}
        <div className="max-w-[680px] mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[38px] font-[750] leading-[36px] md:leading-[46px] text-foreground-950 tracking-[-0.015em]">
            When each pricing model wins — and when it does not
          </h2>
          <p className="text-[15px] text-foreground-500 leading-relaxed mt-4">
            No single model is universally better. The right choice depends on volume, trading style, pair selection, and personal preference. Here is a clear, no-nonsense breakdown.
          </p>
        </div>

        {/* Two columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20">
          {/* Spread-Only Column */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 rounded-full bg-primary-500" />
              <div>
                <h3 className="text-[18px] md:text-[20px] font-[750] text-foreground-950 tracking-[-0.01em]">
                  Spread-Only makes sense when
                </h3>
                <p className="text-[13px] text-foreground-400 mt-0.5">
                  Markup baked into the bid-ask
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {spreadOnlyWinsWhen.map((item, i) => (
                <div key={i} className="pl-4 border-l-2 border-primary-200/60">
                  <div className="text-[15px] font-[700] text-foreground-800 leading-snug">
                    {item.condition}
                  </div>
                  <div className="text-[13px] text-foreground-500 leading-relaxed mt-1.5">
                    {item.detail}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ECN Column */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 rounded-full bg-accent-500" />
              <div>
                <h3 className="text-[18px] md:text-[20px] font-[750] text-foreground-950 tracking-[-0.01em]">
                  ECN / Commission makes sense when
                </h3>
                <p className="text-[13px] text-foreground-400 mt-0.5">
                  Raw spread + visible commission
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {ecnWinsWhen.map((item, i) => (
                <div key={i} className="pl-4 border-l-2 border-accent-200/60">
                  <div className="text-[15px] font-[700] text-foreground-800 leading-snug">
                    {item.condition}
                  </div>
                  <div className="text-[13px] text-foreground-500 leading-relaxed mt-1.5">
                    {item.detail}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Reference — Grouped by Conclusion */}
        <div className="border-t border-background-200/70 pt-14 md:pt-18">
          <div className="mb-10">
            <h3 className="text-[20px] md:text-[24px] font-[750] text-foreground-950 tracking-[-0.01em]">
              Quick reference
            </h3>
            <p className="text-[14px] text-foreground-500 mt-1.5">
              At a glance — which model suits your trading style
            </p>
          </div>

          {/* Pick ECN group — Scalper + Day Trader */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-6 rounded-full bg-accent-500" />
              <h4 className="text-[16px] font-[750] text-foreground-900 tracking-[-0.01em]">
                Pick ECN
              </h4>
              <span className="text-[12px] text-foreground-400">— the math is unambiguous at this volume</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {verdictQuickRef.filter((i) => i.recommendation === 'ECN').map((item) => {
                const style = strengthStyles[item.strength] || strengthStyles.Neutral;
                return (
                  <div key={item.profile} className="flex items-start gap-4 rounded-lg bg-background-50 border border-accent-200/40 px-5 py-4">
                    <div className="shrink-0 mt-0.5">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.10em] ${style.bg} ${style.text}`}>
                        {item.strength}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap mb-1">
                        <span className="text-[15px] font-[700] text-foreground-900">{item.profile}</span>
                        <span className="text-[12px] text-foreground-400">{item.volume}</span>
                      </div>
                      <p className="text-[12px] text-foreground-500 leading-relaxed">{item.note}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Either works group — Swing Trader */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-6 rounded-full bg-secondary-500" />
              <h4 className="text-[16px] font-[750] text-foreground-900 tracking-[-0.01em]">
                Either works
              </h4>
              <span className="text-[12px] text-foreground-400">— cost difference is too small to drive the decision</span>
            </div>
            {verdictQuickRef.filter((i) => i.recommendation === 'Either').map((item) => {
              const style = strengthStyles[item.strength] || strengthStyles.Neutral;
              return (
                <div key={item.profile} className="flex items-start gap-4 rounded-lg bg-background-50 border border-secondary-200/40 px-5 py-4">
                  <div className="shrink-0 mt-0.5">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.10em] ${style.bg} ${style.text}`}>
                      {item.strength}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap mb-1">
                      <span className="text-[15px] font-[700] text-foreground-900">{item.profile}</span>
                      <span className="text-[12px] text-foreground-400">{item.volume}</span>
                    </div>
                    <p className="text-[12px] text-foreground-500 leading-relaxed">{item.note}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pick Spread-Only group — Position Trader */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-6 rounded-full bg-primary-500" />
              <h4 className="text-[16px] font-[750] text-foreground-900 tracking-[-0.01em]">
                Pick Spread-Only
              </h4>
              <span className="text-[12px] text-foreground-400">— simplicity beats marginal savings at low frequency</span>
            </div>
            {verdictQuickRef.filter((i) => i.recommendation === 'Spread-Only').map((item) => {
              const style = strengthStyles[item.strength] || strengthStyles.Neutral;
              return (
                <div key={item.profile} className="flex items-start gap-4 rounded-lg bg-background-50 border border-primary-200/40 px-5 py-4">
                  <div className="shrink-0 mt-0.5">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.10em] ${style.bg} ${style.text}`}>
                      {item.strength}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap mb-1">
                      <span className="text-[15px] font-[700] text-foreground-900">{item.profile}</span>
                      <span className="text-[12px] text-foreground-400">{item.volume}</span>
                    </div>
                    <p className="text-[12px] text-foreground-500 leading-relaxed">{item.note}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}